import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, Brain, Puzzle, Users, Target, Compass } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";

const GOALS_BY_TRACK = {
  comeback: [
    { value: "changing careers", label: "I'm changing careers" },
    { value: "returning to work after a break", label: "I'm returning to work after a break" },
    { value: "seeking growth", label: "I want to grow in my field" },
    { value: "exploring options", label: "I'm exploring my options" },
  ],
  start: [
    { value: "choosing what to study (stream/degree)", label: "Choosing what to study (stream / degree)" },
    { value: "choosing my first job or internship", label: "Choosing my first job or internship" },
    { value: "exploring options after graduation", label: "Exploring options after graduation" },
    { value: "understanding my strengths", label: "Understanding my strengths" },
  ],
};

export const TRACKS = [
  { value: "start", name: "CareerCompass Start", tag: "Students & first careers", desc: "Choosing what to study, a first job or internship." },
  { value: "comeback", name: "CareerCompass Comeback", tag: "Career changers & returners", desc: "Changing direction or returning to work after a break." },
];

const SECTIONS = [
  { key: "cognitive", title: "Cognitive Ability", icon: Brain, color: "#EF4444", type: "mc", hint: "Choose the best answer." },
  { key: "aptitude", title: "Aptitude", icon: Puzzle, color: "#F59E0B", type: "mc", hint: "Choose the best answer." },
  { key: "disc", title: "Personality (DISC)", icon: Users, color: "#10B981", type: "disc", hint: "Pick the option most like you." },
  { key: "holland", title: "Interests (Holland)", icon: Target, color: "#3B82F6", type: "likert", hint: "Rate how much you'd enjoy each." },
];

const LIKERT = [
  { v: "1", t: "Dislike" }, { v: "2", t: "Slightly" }, { v: "3", t: "Neutral" },
  { v: "4", t: "Like" }, { v: "5", t: "Love it" },
];

const Assessment = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [bank, setBank] = useState(null);
  const [step, setStep] = useState(0); // 0 = intro, 1..4 sections
  const [goal, setGoal] = useState("");
  const [track, setTrack] = useState(user?.track || "comeback");
  const GOALS = GOALS_BY_TRACK[track] || GOALS_BY_TRACK.comeback;
  const [background, setBackground] = useState("");
  const [answers, setAnswers] = useState({ cognitive: {}, aptitude: {}, disc: {}, holland: {} });
  const [submitting, setSubmitting] = useState(false);
  const hydrated = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => { if (!loading && !user) navigate("/"); }, [user, loading, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const p = await api.get("/progress");
        const prog = p.data;
        const t = prog?.track || user?.track || "comeback";
        setTrack(t);
        const q = await api.get(`/questions?track=${t}`);
        setBank(q.data);
        if (prog && prog.answers && Object.keys(prog.answers).length > 0) {
          setAnswers({
            cognitive: prog.answers.cognitive || {},
            aptitude: prog.answers.aptitude || {},
            disc: prog.answers.disc || {},
            holland: prog.answers.holland || {},
          });
          setGoal(prog.goal || "");
          setBackground(prog.background || "");
          setStep(prog.step || 0);
          toast.info("Welcome back — we restored your saved progress.");
        }
      } catch (e) { toast.error("Could not load questions"); }
      finally { setTimeout(() => { hydrated.current = true; }, 300); }
    };
    load();
  }, []);

  // Auto-save progress (debounced) after hydration.
  useEffect(() => {
    if (!hydrated.current || submitting) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/progress", { step, goal, background, answers, track }).catch((e) => console.error("Progress autosave failed", e));
    }, 800);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [step, goal, background, answers, track, submitting]);

  const switchTrack = async (t) => {
    if (t === track) return;
    setTrack(t); setGoal("");
    try { const q = await api.get(`/questions?track=${t}`); setBank(q.data); } catch (e) { /* keep current bank */ }
  };

  const totalSteps = SECTIONS.length + 1;
  const progress = (step / totalSteps) * 100;

  const setAns = (sectionKey, qid, value) =>
    setAnswers((prev) => ({ ...prev, [sectionKey]: { ...prev[sectionKey], [qid]: value } }));

  const sectionComplete = (sec) => {
    if (!bank) return false;
    const qs = bank[sec.key];
    return qs.every((q) => answers[sec.key][q.id] !== undefined);
  };

  const next = () => {
    if (step === 0) {
      if (!goal) { toast.error("Please pick what best describes you."); return; }
      setStep(1); window.scrollTo(0, 0); return;
    }
    const sec = SECTIONS[step - 1];
    if (!sectionComplete(sec)) { toast.error("Please answer every question to continue."); return; }
    if (step < SECTIONS.length) { setStep(step + 1); window.scrollTo(0, 0); }
    else submit();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        cognitive: answers.cognitive, aptitude: answers.aptitude,
        disc: answers.disc, holland: answers.holland, goal, background, track,
      };
      const res = await api.post("/assessments", payload);
      toast.success("Your report is ready!");
      navigate(`/report/${res.data.assessment_id}`);
    } catch (e) {
      toast.error("Something went wrong generating your report.");
      setSubmitting(false);
    }
  };

  if (!bank) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Navbar />
        <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </div>
    );
  }

  if (submitting) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]"><Navbar />
        <div className="flex flex-col items-center justify-center h-[70vh] gap-5 text-center px-6" data-testid="generating-report">
          <div className="animate-spin"><Compass className="w-12 h-12 text-primary" /></div>
          <h2 className="font-heading font-extrabold text-2xl text-slate-900">Building your career alignment report…</h2>
          <p className="font-body text-slate-500 max-w-md">We're analyzing your cognition, aptitude, personality and interests. This takes a few seconds.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        {/* progress */}
        <div className="mb-8">
          <div className="flex justify-between font-body text-xs text-slate-500 mb-2">
            <span>Step {step + 1} of {totalSteps + 0}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="assessment-progress" />
        </div>

        <AnimatePresence mode="wait">
          {step === 0 ? (
            <motion.div key="intro" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
              <h1 className="font-heading font-extrabold text-3xl text-slate-900">Let's set the scene</h1>
              <p className="font-body text-slate-600 mt-2">Tell us a little about your situation so your report is tailored to you.</p>

              <div className="mt-8">
                <Label className="font-heading font-bold text-slate-800">Which CareerCompass is right for you?</Label>
                <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="track-picker">
                  {TRACKS.map((t) => (
                    <button key={t.value} type="button" onClick={() => switchTrack(t.value)} data-testid={`track-${t.value}`}
                      className={`text-left rounded-xl border p-4 transition-colors ${track === t.value ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      <p className="font-heading font-extrabold text-slate-900">{t.name}</p>
                      <p className="font-heading font-bold text-[11px] uppercase tracking-wide text-primary mt-0.5">{t.tag}</p>
                      <p className="font-body text-sm text-slate-500 mt-1">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <Label className="font-heading font-bold text-slate-800">What best describes you?</Label>
                <RadioGroup value={goal} onValueChange={setGoal} className="mt-3 grid sm:grid-cols-2 gap-3">
                  {GOALS.map((g) => (
                    <label key={g.value} htmlFor={g.value}
                      className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-colors ${goal === g.value ? "border-primary bg-primary/5" : "border-slate-200 bg-white hover:border-slate-300"}`}
                      data-testid={`goal-${g.value.replace(/\s+/g, '-')}`}>
                      <RadioGroupItem value={g.value} id={g.value} />
                      <span className="font-body text-sm text-slate-700">{g.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="mt-6">
                <Label htmlFor="bg" className="font-heading font-bold text-slate-800">Anything about your background? <span className="font-body font-normal text-slate-400">(optional)</span></Label>
                <Textarea id="bg" value={background} onChange={(e) => setBackground(e.target.value)}
                  placeholder="e.g. Former marketing manager, took 3 years off, interested in tech or design…"
                  className="mt-3 min-h-28 rounded-xl" data-testid="background-input" />
              </div>
            </motion.div>
          ) : (
            <SectionStep key={`sec-${step}`} sec={SECTIONS[step - 1]} bank={bank}
              answers={answers} setAns={setAns} likert={LIKERT} />
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between mt-10">
          <Button variant="ghost" disabled={step === 0}
            onClick={() => { setStep(step - 1); window.scrollTo(0, 0); }}
            data-testid="back-btn" className="font-heading font-bold text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button onClick={next} data-testid="next-btn"
            className="rounded-xl h-12 px-7 font-heading font-bold transition-transform hover:scale-[1.02] active:scale-[0.98]">
            {step === SECTIONS.length ? "See my report" : "Continue"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const SectionStep = ({ sec, bank, answers, setAns, likert }) => {
  const qs = bank[sec.key];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: sec.color + "1a" }}>
          <sec.icon className="w-6 h-6" style={{ color: sec.color }} />
        </div>
        <div>
          <h1 className="font-heading font-extrabold text-2xl text-slate-900">{sec.title}</h1>
          <p className="font-body text-sm text-slate-500">{sec.hint}</p>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        {qs.map((q, qi) => (
          <motion.div key={q.id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: qi * 0.03 }}
            className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`question-${q.id}`}>
            {q.category && <span className="font-body text-xs uppercase tracking-wide text-slate-400">{q.category}</span>}
            <p className="font-heading font-bold text-slate-900 mt-1 mb-4">{qi + 1}. {q.prompt}</p>

            {sec.type === "likert" ? (
              <RadioGroup
                value={answers[sec.key][q.id] !== undefined ? answers[sec.key][q.id].toString() : ""}
                onValueChange={(v) => setAns(sec.key, q.id, parseInt(v))}
                className="grid grid-cols-5 gap-2">
                {likert.map((l) => {
                  const active = answers[sec.key][q.id]?.toString() === l.v;
                  return (
                    <label key={l.v} htmlFor={`${q.id}-${l.v}`}
                      className={`flex flex-col items-center gap-1 rounded-xl border p-2.5 cursor-pointer transition-colors ${active ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`opt-${q.id}-${l.v}`}>
                      <RadioGroupItem value={l.v} id={`${q.id}-${l.v}`} className="sr-only" />
                      <span className={`font-heading font-bold text-lg ${active ? "text-primary" : "text-slate-700"}`}>{l.v}</span>
                      <span className="font-body text-[10px] text-slate-400 text-center leading-tight">{l.t}</span>
                    </label>
                  );
                })}
              </RadioGroup>
            ) : (
              <RadioGroup
                value={answers[sec.key][q.id] !== undefined ? answers[sec.key][q.id].toString() : ""}
                onValueChange={(v) => setAns(sec.key, q.id, parseInt(v))}
                className="space-y-2.5">
                {q.options.map((opt, oi) => {
                  const label = sec.type === "disc" ? opt.text : opt;
                  const active = answers[sec.key][q.id] === oi;
                  return (
                    <label key={oi} htmlFor={`${q.id}-${oi}`}
                      className={`flex items-center gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${active ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"}`}
                      data-testid={`opt-${q.id}-${oi}`}>
                      <RadioGroupItem value={oi.toString()} id={`${q.id}-${oi}`} />
                      <span className="font-body text-sm text-slate-700">{label}</span>
                    </label>
                  );
                })}
              </RadioGroup>
            )}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default Assessment;
